import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { Spinner } from '../../components/ui';
import {
  useGetAdminOfficialsQuery,
  useGetAdminOfficialFiltersQuery,
  useDeleteAdminOfficialMutation,
  useLazyGetAdminOfficialTemplateQuery,
  useImportAdminOfficialsMutation,
  officialName,
  officialEmail,
  officialPhone,
  officialDepartment,
  officialLevel,
  type AdminOfficial,
} from '../../features/adminOfficials/adminOfficialsApi';
import { DownloadIcon, UploadIcon, PlusIcon, KebabIcon, EditSquareIcon, TrashIcon } from '../../assets/icons';

/** Per-department pill colours (fallback = neutral). */
const DEPT_PILL: Record<string, string> = {
  Police: 'border-[#bfdbfe] bg-[#dbeafe] text-[#0061ff]',
  Health: 'border-[#fed7aa] bg-[#fff7ed] text-[#c2410c]',
  Revenue: 'border-[#bbf7d0] bg-[#f0fdf4] text-[#15803d]',
  Education: 'border-[#e9d5ff] bg-[#faf5ff] text-[#7e22ce]',
  'Public Works': 'border-[#fde68a] bg-[#fffbeb] text-[#b45309]',
};
const deptPill = (d: string) => DEPT_PILL[d] ?? 'border-[#e2e8f0] bg-[#f1f5f9] text-[#475569]';

const AVATAR_TINTS = [
  'bg-[#dbeafe] text-[#0061ff]',
  'bg-[#fef3c7] text-[#b45309]',
  'bg-[#dcfce7] text-[#15803d]',
  'bg-[#fae8ff] text-[#a21caf]',
  'bg-[#ffe4e6] text-[#be123c]',
];
const initials = (name: string) => {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '—';
};

const LABEL = 'text-xs font-semibold uppercase tracking-[0.5px] text-[#64748b]';
const OUTLINE_BTN =
  'flex items-center gap-2 rounded-md border border-[#cbd5e1] bg-white px-[17px] py-[9px] text-sm font-medium text-ink transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-60';

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const items: MenuProps['items'] = [
    { key: 'edit', label: 'Edit', icon: <EditSquareIcon className="size-4" /> },
    { type: 'divider' },
    { key: 'delete', label: 'Delete', danger: true, icon: <TrashIcon className="size-4" /> },
  ];
  const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'edit') onEdit();
    else if (key === 'delete') onDelete();
  };
  return (
    <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight">
      <button
        type="button"
        aria-label="Row actions"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex size-8 items-center justify-center rounded-lg text-[#94a3b8] transition-colors hover:bg-brand-shade hover:text-brand"
      >
        <KebabIcon className="h-[15px] w-[3px] rotate-90" />
      </button>
    </Dropdown>
  );
}

export default function AdminDirectoryPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [queryInput, setQueryInput] = useState('');
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState('');
  const [department, setDepartment] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toDelete, setToDelete] = useState<AdminOfficial | null>(null);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(queryInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const { data, isFetching } = useGetAdminOfficialsQuery({
    search,
    department,
    administrativeLevel: level,
    page,
    pageSize,
  });
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [deleteOfficial, { isLoading: isDeleting }] = useDeleteAdminOfficialMutation();
  const [triggerTemplate, { isFetching: isDownloading }] = useLazyGetAdminOfficialTemplateQuery();
  const [importOfficials, { isLoading: isImporting }] = useImportAdminOfficialsMutation();

  // Filter dropdown options — from the dedicated /admin-official/filters/ endpoint.
  const { data: filters } = useGetAdminOfficialFiltersQuery();
  const levelOptions = filters?.administrative_levels ?? [];
  const deptOptions = filters?.departments ?? [];

  const clearFilters = () => {
    setQueryInput('');
    setSearch('');
    setLevel('');
    setDepartment('');
    setPage(1);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteOfficial(toDelete.id).unwrap();
      message.success('Admin official deleted.');
      setToDelete(null);
    } catch {
      message.error('Could not delete the admin official. Please try again.');
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await triggerTemplate().unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'admin-official-template.xlsx';
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download the template. Please try again.');
    }
  };

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const res = await importOfficials({ file }).unwrap();
      const parts = [
        res.created != null ? `${res.created} added` : '',
        res.updated != null ? `${res.updated} updated` : '',
        res.skipped != null ? `${res.skipped} skipped` : '',
      ].filter(Boolean);
      message.success(parts.length ? `Upload complete — ${parts.join(', ')}.` : 'Upload complete.');
    } catch {
      message.error(`Could not upload “${file.name}”. Check the file and try again.`);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-ink">Administrative Level Directory</h1>
          <p className="text-sm text-ink-secondary">Government Directory Management</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={onFilePicked}
          />
          <button type="button" onClick={downloadTemplate} disabled={isDownloading} className={OUTLINE_BTN}>
            <DownloadIcon className="size-4" />
            {isDownloading ? 'Preparing…' : 'Download Template'}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className={OUTLINE_BTN}
          >
            <UploadIcon className="h-[14px] w-[12px]" />
            {isImporting ? 'Uploading…' : 'Bulk Upload'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/directory/new')}
            className="flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <PlusIcon className="size-3.5" />
            Create Admin Official
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 pb-5 pt-6 shadow-card">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex min-w-[200px] flex-1 flex-col gap-2">
            <span className={LABEL}>Name / Email</span>
            <input
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="By Identity..."
              className="w-full rounded-md border border-[#cbd5e1] bg-white px-[13px] py-2.5 text-sm text-ink shadow-card placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <div className="flex w-[200px] shrink-0 flex-col gap-2">
            <span className={LABEL}>Administrative Level</span>
            <Select
              className="w-full"
              placeholder="Levels"
              allowClear
              value={level || undefined}
              onChange={(v) => {
                setLevel(v ?? '');
                setPage(1);
              }}
              options={levelOptions.map((l) => ({ value: l, label: l }))}
            />
          </div>
          <div className="flex w-[200px] shrink-0 flex-col gap-2">
            <span className={LABEL}>Department</span>
            <Select
              className="w-full"
              placeholder="Department"
              allowClear
              value={department || undefined}
              onChange={(v) => {
                setDepartment(v ?? '');
                setPage(1);
              }}
              options={deptOptions.map((d) => ({ value: d, label: d }))}
            />
          </div>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-md bg-[#eef2f7] px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-brand-shade"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-[rgba(248,250,252,0.5)] text-xs font-semibold uppercase tracking-[0.4px] text-[#64748b]">
                <th className="px-6 py-4">Admin Official</th>
                <th className="px-6 py-4">Contact</th>
                <th className="px-6 py-4">Department</th>
                <th className="px-6 py-4">Administrative Level</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isFetching ? (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center">
                    <Spinner />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-ink-secondary">
                    No admin officials match your filters.
                  </td>
                </tr>
              ) : (
                rows.map((o, i) => {
                  const name = officialName(o);
                  const dept = officialDepartment(o);
                  return (
                    <tr key={o.id} className="border-b border-[#e2e8f0] last:border-b-0 hover:bg-[#f8fafc]">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <span
                            className={[
                              'flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                              AVATAR_TINTS[i % AVATAR_TINTS.length],
                            ].join(' ')}
                          >
                            {initials(name)}
                          </span>
                          <span className="text-sm font-semibold text-ink">{name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col">
                          <span className="text-sm text-ink">{officialEmail(o) || '—'}</span>
                          <span className="text-xs text-ink-secondary">{officialPhone(o)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        {dept ? (
                          <span
                            className={[
                              'inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium',
                              deptPill(dept),
                            ].join(' ')}
                          >
                            {dept}
                          </span>
                        ) : (
                          <span className="text-sm text-[#94a3b8]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-sm text-ink">{officialLevel(o) || '—'}</td>
                      <td className="px-6 py-5 text-right">
                        <RowActions
                          onEdit={() => navigate(`/admin/directory/${o.id}/edit`)}
                          onDelete={() => setToDelete(o)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-[#e2e8f0] px-6 py-4">
          <Pagination
            rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
            rangeEnd={Math.min(page * pageSize, total)}
            total={total}
            noun="officials"
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(v) => {
              setPageSize(v);
              setPage(1);
            }}
          />
        </div>
      </div>

      <ConfirmDeleteModal
        open={toDelete !== null}
        title="Delete Admin Official"
        message={
          toDelete ? (
            <>
              Are you sure you want to delete{' '}
              <span className="font-semibold text-ink">“{officialName(toDelete)}”</span>? This action
              cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        loading={isDeleting}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
